import { useState } from "react";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Drawer from "@mui/material/Drawer";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { SlidersHorizontal } from "lucide-react";
import type { TwoStageFilterProps } from "../TwoStageFilter";

/** "Filter by account status" → "Account status". */
function sectionTitle(modeLabel: string): string {
  const rest = modeLabel.replace(/^filter by\s+/i, "");
  return rest.charAt(0).toUpperCase() + rest.slice(1);
}

/**
 * Phone version of TwoStageFilter: a full-width search with a Filter button
 * that opens a bottom sheet. The sheet lists every mode's options as chips;
 * one tap sets mode and value together and closes the sheet. The active
 * filter shows under the search as a chip that clears it. Same props and
 * same onChange contract as the desktop filter.
 */
export function PhoneFilterBar<V extends string>(props: TwoStageFilterProps<V>) {
  const {
    modes,
    noneValue = "none",
    noneLabel = "All",
    search,
    onSearchChange,
    searchPlaceholder = "Search by name or email...",
    searchTestId,
    mode,
    value,
    onChange,
    trailing,
  } = props;
  const [open, setOpen] = useState(false);

  const activeMode = modes.find((m) => m.value === mode);
  const activeOption = activeMode?.options.find((o) => o.value === value);
  const filterLabel = activeMode
    ? `${sectionTitle(activeMode.label)}: ${activeOption?.label ?? "any"}`
    : null;

  function choose(nextMode: string, nextValue: V | null) {
    onChange({ mode: nextMode, value: nextValue });
    setOpen(false);
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          size="small"
          fullWidth
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          slotProps={{
            htmlInput: {
              "aria-label": searchPlaceholder,
              ...(searchTestId ? { "data-testid": searchTestId } : {}),
            },
          }}
        />
        <Badge color="primary" variant="dot" invisible={!activeMode}>
          <Button
            variant="outlined"
            onClick={() => setOpen(true)}
            startIcon={<SlidersHorizontal size={16} />}
            data-testid="phone-filter-button"
            sx={{ flexShrink: 0 }}
          >
            Filter
          </Button>
        </Badge>
      </Stack>

      {(filterLabel || trailing) && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mt: 1.5, minHeight: 32 }}
        >
          {filterLabel && (
            <Chip
              label={filterLabel}
              onDelete={() => choose(noneValue, null)}
              data-testid="phone-filter-active"
            />
          )}
          {trailing && <Box sx={{ ml: "auto" }}>{trailing}</Box>}
        </Stack>
      )}

      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        slotProps={{
          paper: {
            sx: { borderTopLeftRadius: 16, borderTopRightRadius: 16, px: 2, pt: 1.5, pb: 3 },
          },
        }}
      >
        <Box
          aria-hidden
          sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: "divider", mx: "auto", mb: 2 }}
        />
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
          Filter
        </Typography>
        <Chip
          label={noneLabel}
          color={activeMode ? "default" : "primary"}
          variant={activeMode ? "outlined" : "filled"}
          onClick={() => choose(noneValue, null)}
          sx={{ mb: 2, alignSelf: "flex-start" }}
        />
        {modes.map((m) => (
          <Box key={m.value} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              {sectionTitle(m.label)}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {m.options.map((opt) => {
                const selected = mode === m.value && value === opt.value;
                return (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    color={selected ? "primary" : "default"}
                    variant={selected ? "filled" : "outlined"}
                    onClick={() => choose(m.value, opt.value)}
                    data-testid={`phone-filter-${m.value}-${opt.value}`}
                  />
                );
              })}
            </Box>
          </Box>
        ))}
      </Drawer>
    </Box>
  );
}
